import { useCallback, useEffect, useRef } from 'react';
import {
  CloudWatchLogsClient,
  PutLogEventsCommand
} from '@aws-sdk/client-cloudwatch-logs';
import * as Sentry from '@sentry/react';

import { Config } from '@nw/shared/config';
import { useFileUploader } from '@nw/shared/data-access';
import {
  getDeviceBrowserName,
  getDeviceFullBrowserVersion,
  getDeviceOSName,
  getDeviceOSVersion,
  getDeviceTypeAtAnalyticsTracking,
  getUserInfoDetails
} from '@nw/shared/utils';

import {
  CLOUDWATCH_LOG_STREAM_NAME,
  CLOUDWATCH_REGION,
  CloudWatchEventNames,
  CloudWatchEventTypes
} from '../../constants/CloudWatchEventConstants';
import {
  CloudWatchEventName,
  CloudWatchEventPayload
} from '../../types/cloudWatchTypes';

const MAX_BUFFER_SIZE = 25;
const MAX_BUFFER_CAPACITY = 200;
const FLUSH_INTERVAL_MS = 5000;
const SECTION_HEARTBEAT_INTERVAL_MS = 60000;
const MAX_RETRIES = 2;
const MAX_AUTH_RETRIES = 3;
const VISIBILITY_DEBOUNCE_MS = 300;

export const useCloudWatchLogger = () => {
  const clientRef = useRef<CloudWatchLogsClient | null>(null);
  const bufferRef = useRef<CloudWatchEventPayload[]>([]);
  const sequenceTokenRef = useRef<string | undefined>(undefined);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isMountedRef = useRef(true);
  const isFlushingRef = useRef(false);
  const isRefreshingCredentialsRef = useRef(false);
  const retryCountRef = useRef(0);
  const consecutiveAuthFailuresRef = useRef(0);
  const visibilityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const sectionEntryRef = useRef<{
    sectionId: string;
    subSectionId: string;
    userId: string;
    entryTime: number;
    entryTimeISO: string;
    accumulatedMs: number;
    lastVisibleTime: number;
    isVisible: boolean;
  } | null>(null);
  const sectionHeartbeatTimerRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);

  // Session-level tracking for total time spent
  const sessionTrackingRef = useRef<{
    sessionStartTime: number;
    sessionStartTimeISO: string;
    pausedAt: number | null;
  } | null>(null);

  const { refreshAwsCredentials } = useFileUploader();

  const flushRef = useRef<() => void>(() => undefined);

  const refreshClient = useCallback(async (): Promise<boolean> => {
    if (!refreshAwsCredentials) return false;
    if (isRefreshingCredentialsRef.current) return false;

    isRefreshingCredentialsRef.current = true;
    clientRef.current = null;

    try {
      const credentials = await refreshAwsCredentials();
      if (!credentials?.accessKeyId) return false;

      clientRef.current = new CloudWatchLogsClient({
        region: CLOUDWATCH_REGION,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.awsSessionToken
        }
      });
      sequenceTokenRef.current = undefined;
      return true;
    } catch (error) {
      Sentry.addBreadcrumb({
        category: 'cloudwatch',
        message: 'Failed to refresh AWS credentials',
        data: { error }
      });
      return false;
    } finally {
      isRefreshingCredentialsRef.current = false;
    }
  }, [refreshAwsCredentials]);

  useEffect(() => {
    refreshClient().then((ok) => {
      if (ok && isMountedRef.current && bufferRef.current.length > 0) {
        flushRef.current();
      }
    });
  }, [refreshClient]);

  const flush = useCallback(async () => {
    if (isFlushingRef.current) return;
    if (!isMountedRef.current) return;
    if (bufferRef.current.length === 0) return;
    if (isRefreshingCredentialsRef.current) return;

    if (!clientRef.current) {
      const newClient = await refreshClient();
      if (!newClient || !clientRef.current) return;
    }

    isFlushingRef.current = true;
    const events = [...bufferRef.current];

    try {
      const command = new PutLogEventsCommand({
        logGroupName: Config['CLOUDWATCH_LOG_GROUP_NAME'],
        logStreamName: CLOUDWATCH_LOG_STREAM_NAME,
        logEvents: events.map((e) => ({
          timestamp: Date.now(),
          message: JSON.stringify(e)
        })),
        sequenceToken: sequenceTokenRef.current
      });

      const response = await clientRef.current.send(command);
      sequenceTokenRef.current = response.nextSequenceToken;

      bufferRef.current = bufferRef.current.slice(events.length);
      retryCountRef.current = 0;
      consecutiveAuthFailuresRef.current = 0;
    } catch (error: any) {
      if (
        error.name === 'ExpiredTokenException' ||
        error.name === 'UnrecognizedClientException'
      ) {
        consecutiveAuthFailuresRef.current++;

        if (consecutiveAuthFailuresRef.current > MAX_AUTH_RETRIES) {
          bufferRef.current = bufferRef.current.slice(events.length);
          consecutiveAuthFailuresRef.current = 0;
        } else {
          isFlushingRef.current = false;
          const refreshed = await refreshClient();
          if (refreshed && isMountedRef.current && bufferRef.current.length > 0) {
            flush();
          }
          return;
        }
      } else if (error.name === 'InvalidSequenceTokenException') {
        sequenceTokenRef.current = error.expectedSequenceToken;
      } else if (
        error.name === 'ThrottlingException' ||
        error.name === 'ServiceUnavailableException' ||
        error.name?.includes('Network')
      ) {
        retryCountRef.current++;
        if (retryCountRef.current >= MAX_RETRIES) {
          bufferRef.current = bufferRef.current.slice(events.length);
          retryCountRef.current = 0;
        } else {
          const backoffMs = 1000 * Math.pow(2, retryCountRef.current);
          setTimeout(() => {
            if (isMountedRef.current) flush();
          }, backoffMs);
        }
      } else {
        bufferRef.current = bufferRef.current.slice(events.length);
        retryCountRef.current = 0;
      }

      Sentry.addBreadcrumb({
        category: 'cloudwatch',
        message: `PutLogEvents failed: ${error.name}`,
        data: {
          eventCount: events.length,
          retryCount: retryCountRef.current,
          authFailures: consecutiveAuthFailuresRef.current
        }
      });
    } finally {
      isFlushingRef.current = false;
    }
  }, [refreshClient]);

  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  useEffect(() => {
    flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS);
    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flush();
    };
  }, [flush]);

  const buildPayload = useCallback(
    (
      eventName: string,
      eventType: string,
      pageId: string,
      userId: string,
      additionalData?: Partial<CloudWatchEventPayload>
    ): CloudWatchEventPayload => {
      const { browserSessionId } = getUserInfoDetails();
      return {
        event_type: eventType,
        event_name: eventName,
        user_id: userId || '',
        session_id: browserSessionId,
        page_url: window.location.href,
        page_id: pageId,
        record_timestamp: new Date().toISOString(),
        device: getDeviceTypeAtAnalyticsTracking(),
        browser: getDeviceBrowserName(),
        browser_version: getDeviceFullBrowserVersion(),
        os_name: getDeviceOSName(),
        os_version: getDeviceOSVersion(),
        ...additionalData
      };
    },
    []
  );

  const logEvent = useCallback(
    (
      eventName: CloudWatchEventName,
      eventType: string,
      pageId: string,
      userId: string,
      additionalData?: Partial<CloudWatchEventPayload>,
      options?: { force?: boolean }
    ) => {
      if (!Config['CLOUDWATCH_LOG_GROUP_NAME']) return;

      if (bufferRef.current.length >= MAX_BUFFER_CAPACITY) {
        bufferRef.current.shift();
        Sentry.addBreadcrumb({
          category: 'cloudwatch',
          message: 'Buffer capacity exceeded, dropping oldest event',
          level: 'warning'
        });
      }

      const payload = buildPayload(
        eventName,
        eventType,
        pageId,
        userId,
        additionalData
      );
      bufferRef.current.push(payload);

      if (options?.force || bufferRef.current.length >= MAX_BUFFER_SIZE) {
        flush();
      }
    },
    [buildPayload, flush]
  );

  const sendSectionDurationEvent = useCallback(
    (
      exitType: string,
      pageId: string,
      userId: string,
      options?: { force?: boolean }
    ) => {
      if (!sectionEntryRef.current || !sessionTrackingRef.current) return;

      const {
        accumulatedMs,
        lastVisibleTime,
        isVisible,
        sectionId,
        subSectionId
      } = sectionEntryRef.current;

      // Calculate section-specific duration
      let sectionDurationMs = accumulatedMs;
      if (isVisible) {
        sectionDurationMs += Date.now() - lastVisibleTime;
      }
      const watchDurationSec = Math.round(sectionDurationMs / 1000);

      // Calculate total session duration
      const totalSessionMs =
        Date.now() - sessionTrackingRef.current.sessionStartTime;
      const totalSessionTimeSec = Math.round(totalSessionMs / 1000);

      const exitPageId = subSectionId || sectionId;
      const effectivePageId = exitPageId || pageId || '';
      const effectiveUserId = userId || sectionEntryRef.current?.userId || '';

      // Only send if watch duration is greater than 0
      if (watchDurationSec > 0 && totalSessionTimeSec > 0) {
        logEvent(
          CloudWatchEventNames.USER_TOTAL_TIME_SPENT_IN_PAGE_EVENT,
          CloudWatchEventTypes.TOTAL_TIME_SPENT,
          effectivePageId,
          effectiveUserId,
          {
            session_start_time: sessionTrackingRef.current.sessionStartTimeISO,
            session_end_time: new Date().toISOString(),
            watch_duration_sec: watchDurationSec,
            total_session_time_spent_in_seconds: totalSessionTimeSec,
            page_exit_type: exitType
          },
          options
        );
      }
    },
    [logEvent]
  );

  const trackSectionEntry = useCallback(
    (sectionId: string, subSectionId: string, userId: string) => {
      const now = Date.now();

      if (!sessionTrackingRef.current) {
        sessionTrackingRef.current = {
          sessionStartTime: now,
          sessionStartTimeISO: new Date(now).toISOString(),
          pausedAt: null
        };
      }

      sectionEntryRef.current = {
        sectionId,
        subSectionId,
        userId,
        entryTime: now,
        entryTimeISO: new Date(now).toISOString(),
        accumulatedMs: 0,
        lastVisibleTime: now,
        isVisible: true
      };

      if (sectionHeartbeatTimerRef.current) {
        clearInterval(sectionHeartbeatTimerRef.current);
      }

      sectionHeartbeatTimerRef.current = setInterval(() => {
        if (sectionEntryRef.current && sectionEntryRef.current.isVisible) {
          const {
            subSectionId: subId,
            sectionId: secId,
            userId: uid
          } = sectionEntryRef.current;
          sendSectionDurationEvent('heartbeat', subId || secId, uid);

          sectionEntryRef.current.accumulatedMs = 0;
          sectionEntryRef.current.lastVisibleTime = Date.now();
        }
      }, SECTION_HEARTBEAT_INTERVAL_MS);
    },
    [sendSectionDurationEvent]
  );

  const trackSectionExit = useCallback(
    (
      exitType: string,
      pageId: string,
      userId: string,
      options?: { force?: boolean }
    ) => {
      if (!sectionEntryRef.current) return;

      if (sectionHeartbeatTimerRef.current) {
        clearInterval(sectionHeartbeatTimerRef.current);
        sectionHeartbeatTimerRef.current = null;
      }

      sendSectionDurationEvent(exitType, pageId, userId, options);

      if (options?.force) {
        flush();
      }

      sectionEntryRef.current = null;
    },
    [flush, sendSectionDurationEvent]
  );

  const trackSessionEnd = useCallback(
    (userId: string) => {
      if (sectionEntryRef.current) {
        const { subSectionId, sectionId } = sectionEntryRef.current;
        sendSectionDurationEvent(
          'session_end',
          subSectionId || sectionId,
          userId
        );
      }

      // Reset session tracking
      sessionTrackingRef.current = null;

      flush();
    },
    [flush, sendSectionDurationEvent]
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (visibilityDebounceRef.current) {
        clearTimeout(visibilityDebounceRef.current);
      }

      visibilityDebounceRef.current = setTimeout(() => {
        if (document.visibilityState === 'hidden') {
          // Stop heartbeat timer
          if (sectionHeartbeatTimerRef.current) {
            clearInterval(sectionHeartbeatTimerRef.current);
            sectionHeartbeatTimerRef.current = null;
          }

          // Pause session tracking
          if (
            sessionTrackingRef.current &&
            !sessionTrackingRef.current.pausedAt
          ) {
            sessionTrackingRef.current.pausedAt = Date.now();
          }

          // Mark section as not visible
          if (sectionEntryRef.current) {
            sectionEntryRef.current.isVisible = false;
          }

          flush();
        } else if (document.visibilityState === 'visible') {
          // Resume session tracking
          if (
            sessionTrackingRef.current &&
            sessionTrackingRef.current.pausedAt
          ) {
            const pauseDuration =
              Date.now() - sessionTrackingRef.current.pausedAt;
            sessionTrackingRef.current.sessionStartTime += pauseDuration;
            sessionTrackingRef.current.pausedAt = null;
          }

          if (sectionEntryRef.current) {
            sectionEntryRef.current.lastVisibleTime = Date.now();
            sectionEntryRef.current.isVisible = true;

            if (!sectionHeartbeatTimerRef.current) {
              sectionHeartbeatTimerRef.current = setInterval(() => {
                if (
                  sectionEntryRef.current &&
                  sectionEntryRef.current.isVisible
                ) {
                  const {
                    subSectionId: subId,
                    sectionId: secId,
                    userId: uid
                  } = sectionEntryRef.current;
                  sendSectionDurationEvent('heartbeat', subId || secId, uid);

                  sectionEntryRef.current.accumulatedMs = 0;
                  sectionEntryRef.current.lastVisibleTime = Date.now();
                }
              }, SECTION_HEARTBEAT_INTERVAL_MS);
            }
          }
        }
      }, VISIBILITY_DEBOUNCE_MS);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityDebounceRef.current) {
        clearTimeout(visibilityDebounceRef.current);
      }
    };
  }, [flush, sendSectionDurationEvent]);

  useEffect(() => {
    const handlePageHide = () => {
      flush();
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [flush]);

  useEffect(() => {
    const handleOnline = () => {
      if (bufferRef.current.length > 0) {
        flush();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [flush]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    logEvent,
    trackSectionEntry,
    trackSectionExit,
    trackSessionEnd,
    flush
  };
};
